import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import { startOfMonth, endOfMonth, format } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";
import { convertCurrencyAmount } from "@/lib/fx-rates";
import { logger } from "@/lib/logger";
import { isBuukdPlatformOnlyPaymentContext } from "@/lib/platform-settlements";

interface PlatformPaymentWithDetails {
  id: string;
  business_id: string;
  context: string;
  context_id: string | null;
  context_meta: any;
  payer_email: string;
  payer_name: string | null;
  customer_id: string | null;
  currency: string;
  gross_amount: number;
  fee_amount: number;
  net_amount: number;
  provider: string;
  provider_reference: string | null;
  provider_transaction_id: number | null;
  status: "pending" | "success" | "failed" | "abandoned" | "refunded";
  initialized_at: string;
  paid_at: string | null;
  payout_status: string;
  created_at: string;
  updated_at: string;
  // Related data
  customer?: {
    name: string;
    email: string;
  } | null;
  appointment?: {
    id: string;
    start_datetime: string;
    service?: {
      name: string;
    };
  } | null;
  invoice?: {
    id: string;
    invoice_number: string;
  } | null;
}

interface BillingMetrics {
  // Current month metrics
  appointmentsThisMonth: number;
  staffCount: number;
  locationsCount: number;
  customersCount: number;
  revenueThisMonth: number;
  paidPaymentsCount: number;
  
  // Usage limits (can be configured per plan)
  appointmentLimit: number | null; // null = unlimited
  staffLimit: number | null;
  locationLimit: number | null;
  customerLimit: number | null; // cumulative
  smsCampaignLimit: number | null;
  emailCampaignLimit: number | null;
  
  // Campaign counts (this month; sent + scheduled)
  smsCampaignsThisMonth: number;
  emailCampaignsThisMonth: number;
  
  // Payment history (from platform_payments)
  payments: PlatformPaymentWithDetails[];
  totalRevenue: number;
  
  // Invoice-like data (generated from payments)
  invoices: Array<{
    id: string;
    date: string;
    amount: number;
    currency: string;
    status: string;
    paymentId: string;
    reference: string;
    type: string;
  }>;
}

const BILLING_QUERY_TIMEOUT_MS = 10000;
const PAYMENT_HISTORY_LIMIT = 100;
const PAYMENT_TOTALS_PAGE_SIZE = 1000;

const EMPTY_BILLING_METRICS: BillingMetrics = {
  appointmentsThisMonth: 0,
  staffCount: 0,
  locationsCount: 0,
  customersCount: 0,
  revenueThisMonth: 0,
  paidPaymentsCount: 0,
  appointmentLimit: null,
  staffLimit: null,
  locationLimit: null,
  customerLimit: null,
  smsCampaignLimit: null,
  emailCampaignLimit: null,
  smsCampaignsThisMonth: 0,
  emailCampaignsThisMonth: 0,
  payments: [],
  totalRevenue: 0,
  invoices: [],
};

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error || "Unknown error");

const withTimeout = async <T,>(
  promise: PromiseLike<T>,
  label: string,
  timeoutMs = BILLING_QUERY_TIMEOUT_MS
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs / 1000}s`));
    }, timeoutMs);

    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });

const loadSupabaseData = async <T,>(
  label: string,
  query: PromiseLike<{ data: T | null; error: unknown }>,
  fallback: T,
  context?: Record<string, unknown>
): Promise<T> => {
  try {
    const { data, error } = await withTimeout(query, label);
    if (error) throw error;
    return data ?? fallback;
  } catch (error) {
    logger.warn(`Billing ${label} unavailable`, {
      ...context,
      error: getErrorMessage(error),
    });
    return fallback;
  }
};

const loadSupabaseCount = async (
  label: string,
  query: PromiseLike<{ count: number | null; error: unknown }>,
  context?: Record<string, unknown>
): Promise<number> => {
  try {
    const { count, error } = await withTimeout(query, label);
    if (error) throw error;
    return count ?? 0;
  } catch (error) {
    logger.warn(`Billing ${label} count unavailable`, {
      ...context,
      error: getErrorMessage(error),
    });
    return 0;
  }
};

const loadSuccessfulPaymentsForTotals = async (businessId: string) => {
  const rows: Array<{ gross_amount: number | string | null; currency: string | null; paid_at: string | null }> = [];

  for (let from = 0; ; from += PAYMENT_TOTALS_PAGE_SIZE) {
    const to = from + PAYMENT_TOTALS_PAGE_SIZE - 1;
    const { data, error } = await withTimeout(
      supabase
        .from("platform_payments" as any)
        .select("gross_amount, currency, paid_at")
        .eq("business_id", businessId)
        .not("context", "eq", "appointment_reservation")
        .not("context", "eq", "appointment_group_reservation")
        .not("context", "eq", "platform_signup_fee")
        .not("context", "eq", "platform_subscription_setup")
        .eq("status", "success")
        .order("created_at", { ascending: false })
        .range(from, to),
      "successful payment totals"
    );

    if (error) throw error;

    const page = ((data || []) as unknown as Array<{
      gross_amount: number | string | null;
      currency: string | null;
      paid_at: string | null;
    }>);
    rows.push(...page);

    if (page.length < PAYMENT_TOTALS_PAGE_SIZE) {
      return rows;
    }
  }
};

export function useBilling() {
  const { business } = useBusiness();
  const [metrics, setMetrics] = useState<BillingMetrics>(EMPTY_BILLING_METRICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBillingData = useCallback(async () => {
    if (!business) {
      setError("No business found for this account. Make sure you're signed into the business owner account.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      const displayCurrency = business.currency || "GHS";
      const convertRevenue = (amount: number, sourceCurrency?: string | null) =>
        convertCurrencyAmount(amount, sourceCurrency || displayCurrency, displayCurrency);
      
      // Calculate billing period: from subscription_last_paid_at to subscription_next_payment_at
      // If subscription_last_paid_at is not set, fall back to current calendar month
      let periodStart: Date;
      let periodEnd: Date;
      
      if ((business as any)?.subscription_last_paid_at) {
        periodStart = new Date((business as any).subscription_last_paid_at);
        // Use subscription_next_payment_at if set, otherwise calculate +1 month from last_paid
        if ((business as any)?.subscription_next_payment_at) {
          periodEnd = new Date((business as any).subscription_next_payment_at);
        } else {
          periodEnd = new Date(periodStart);
          periodEnd.setMonth(periodEnd.getMonth() + 1);
        }
      } else {
        // Fallback to calendar month if no payment history
        periodStart = startOfMonth(now);
        periodEnd = endOfMonth(now);
      }
      
      // Only count usage within the current billing period (up to now)
      const periodStartISO = periodStart.toISOString();
      const periodEndISO = now < periodEnd ? now.toISOString() : periodEnd.toISOString();

      const primaryModule = String((business as any)?.primary_module || "appointments");
      const isCateringServices = primaryModule === "catering_services" || primaryModule === "food_vendors";

      // Usage unit for billing:
      // - Appointments module: appointments created within billing period
      // - Catering services module: food_orders created within billing period
      const usageQuery = isCateringServices
        ? supabase
            .from("food_orders" as any)
            .select("id", { count: "exact", head: true })
            .eq("business_id", business.id)
            .gte("created_at", periodStartISO)
            .lte("created_at", periodEndISO)
        : supabase
            .from("appointments")
            .select("id", { count: "exact", head: true })
            .eq("business_id", business.id)
            .gte("created_at", periodStartISO)
            .lte("created_at", periodEndISO);

      const planId = (business as any).plan_id || "starter";
      const queryContext = { businessId: business.id };
      const [usageCount, staffCount, locationsCount, customersCount, plan] = await Promise.all([
        loadSupabaseCount("usage", usageQuery, queryContext),
        loadSupabaseCount(
          "staff",
          supabase
            .from("staff")
            .select("id", { count: "exact", head: true })
            .eq("business_id", business.id)
            .eq("active", true),
          queryContext
        ),
        loadSupabaseCount(
          "locations",
          supabase
            .from("locations")
            .select("id", { count: "exact", head: true })
            .eq("business_id", business.id),
          queryContext
        ),
        loadSupabaseCount(
          "customers",
          supabase
            .from("customers")
            .select("id", { count: "exact", head: true })
            .eq("business_id", business.id),
          queryContext
        ),
        loadSupabaseData(
          "subscription plan",
          supabase.from("subscription_plans").select("*").eq("id", planId).maybeSingle(),
          null as Tables<"subscription_plans"> | null,
          queryContext
        ),
      ]);

      let effectivePlan: any = plan;
      if (!effectivePlan && planId !== "starter") {
        effectivePlan = await loadSupabaseData(
          "starter subscription plan",
          supabase.from("subscription_plans").select("*").eq("id", "starter").maybeSingle(),
          null as Tables<"subscription_plans"> | null,
          queryContext
        );
      }

      const platformPayments = await loadSupabaseData(
        "payment history",
        supabase
          .from("platform_payments" as any)
          .select(`
            *,
            customer:customers(name, email)
          `)
          .eq("business_id", business.id)
          .not("context", "eq", "appointment_reservation")
          .not("context", "eq", "appointment_group_reservation")
          .not("context", "eq", "platform_signup_fee")
          .not("context", "eq", "platform_subscription_setup")
          .order("created_at", { ascending: false })
          .limit(PAYMENT_HISTORY_LIMIT),
        [] as any[],
        queryContext
      );

      const enrichedPayments = ((platformPayments || []) as PlatformPaymentWithDetails[]).filter(
        (p) => !isBuukdPlatformOnlyPaymentContext(p.context),
      );
      const successfulPaymentsForTotals = await loadSuccessfulPaymentsForTotals(business.id);

      // Calculate revenue this billing period (all successful payments from all contexts: 
      // appointment_deposit, invoice_payment, membership_payment, membership_renewal, 
      // day_pass_payment, event_registration, etc.)
      const paymentsThisPeriod = successfulPaymentsForTotals.filter(
        (p) => p.paid_at && new Date(p.paid_at) >= periodStart && new Date(p.paid_at) <= periodEnd
      );
      const revenueThisMonth = paymentsThisPeriod.reduce(
        (sum, p) => sum + convertRevenue(parseFloat(String(p.gross_amount || 0)), p.currency),
        0
      );

      // Calculate total revenue (all successful payments from all contexts)
      const totalRevenue = successfulPaymentsForTotals.reduce(
        (sum, p) => sum + convertRevenue(parseFloat(String(p.gross_amount || 0)), p.currency),
        0
      );

      // Fetch marketing campaigns for counts (sent + scheduled) in current billing period
      const campaigns = await loadSupabaseData(
        "marketing campaign usage",
        supabase
          .from("marketing_campaigns")
          .select("id, type, status, sent_at, scheduled_at, created_at")
          .eq("business_id", business.id)
          .in("status", ["sent", "scheduled"])
          .gte("created_at", periodStartISO)
          .lte("created_at", periodEndISO),
        [] as Array<{ type: string }>,
        queryContext
      );

      const smsCampaignsThisMonth =
        campaigns?.filter((c) => c.type === "sms").length || 0;
      const emailCampaignsThisMonth =
        campaigns?.filter((c) => c.type === "email").length || 0;

      // Generate invoice-like data from platform payments (only successful)
      const invoices = (enrichedPayments || [])
        .filter((p) => p.status === "success")
        .map((p) => ({
          id: `INV-${format(new Date(p.created_at), "yyyy-MM")}-${p.id.slice(0, 8).toUpperCase()}`,
          date: format(new Date(p.paid_at || p.created_at), "MMM d, yyyy"),
          amount: parseFloat(String(p.gross_amount || 0)),
          currency: p.currency || business.currency || "GHS",
          status: p.status,
          paymentId: p.id,
          reference: p.provider_reference || p.id,
          type: p.context,
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setMetrics({
        appointmentsThisMonth: usageCount,
        staffCount,
        locationsCount,
        customersCount,
        revenueThisMonth,
        paidPaymentsCount: successfulPaymentsForTotals.length,
        appointmentLimit: effectivePlan?.booking_limit_monthly ?? null,
        staffLimit: effectivePlan?.staff_limit ?? null,
        locationLimit: effectivePlan?.location_limit ?? null,
        customerLimit: effectivePlan?.customer_limit_cumulative ?? null,
        smsCampaignLimit: effectivePlan?.sms_campaign_limit_monthly ?? null,
        emailCampaignLimit: effectivePlan?.email_campaign_limit_monthly ?? null,
        smsCampaignsThisMonth,
        emailCampaignsThisMonth,
        payments: enrichedPayments,
        totalRevenue,
        invoices,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch billing data");
    } finally {
      setLoading(false);
    }
  }, [business]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  return { metrics, loading, error, refetch: fetchBillingData };
}

